import type { StoreItem } from "../StoreItem";
import type { UnfilledSymbolName } from "@core/ui/svg-icons/symbols/SymbolName";
import type { SimulationItem } from "../simulation/SimulationItem";
import type { TargetItem } from "../target/TargetItem";
import type { ResourceItem } from "../resource/ResourceItem";
import type { IndexItem } from "../index/IndexItem";
import type { ActionActivationItem, IncidentActivationItem, ScheduledEffectActivationItem } from "./ChartDataTypes";
import type { SimulationRoundItem } from "../simulationRound/SimulationRoundItem";
import type { Uid } from "@/helpers/utility/Id";

export type ChartType = "line" | "bar" | "spider" | "radar";

export type SelectableChartItemType =
  | "simulationRound"
  | "simulation"
  | "target"
  | "resource"
  | "index"
  | "actionActivation"
  | "incidentActivation"
  | "scheduledEffectActivation";

export type SelectableChartItem<
  T extends SelectableChartItemType = SelectableChartItemType
> = {
  type: T;
  id: SelectableChartItemIds[T];
};

export type SelectableChartItemIds = {
  simulationRound: number;
  simulation: number;
  target: number;
  resource: number;
  index: number;
  incidentActivation: string;
  actionActivation: string;
  scheduledEffectActivation: string;
};

export type ItemForChart<Type extends SelectableChartItemType> =
  Type extends "simulation"
    ? SimulationItem
    : Type extends "target"
    ? TargetItem
    : Type extends "resource"
    ? ResourceItem
    : Type extends "index"
    ? IndexItem
    : Type extends "actionActivation"
    ? ActionActivationItem
    : Type extends "incidentActivation"
    ? IncidentActivationItem
    : Type extends "scheduledEffectActivation"
    ? ScheduledEffectActivationItem
    : Type extends "simulationRound"
    ? SimulationRoundItem
    : SelectableChartItem<Type>;

export type ItemConfig = {
  open: boolean;
  color?: string | null;
  symbol?: UnfilledSymbolName;
  [simulationId: number]: {
    color?: string | null;
    symbol?: string;
  };
  subgroups?: { [subgroup: string]: ItemConfig };
};

export type ChartSettings = {
  zoomed: boolean;
  width: number | null;
  height: number | null;
  scaleColorsVisible: boolean;
  ticksVisible: boolean;
  xAxis: string;
  yAxis: "value" | "effect" | "gain" | "impulseSum" | "impulse";

  xAxisLabelsVisible: boolean;
  xAxisTitleVisible: boolean;
  xAxisLabelsShortened: boolean;
  yAxisLabelsVisible: boolean;
  yAxisTitleVisible: boolean;
  yAxisLabelsShortened: boolean;
  groupLabelsVisible: boolean;
  groupLabelsShortened: boolean;

  fontSize: number;
  filterZeroValues: boolean;
  subValuesVisible: boolean;
  endPointsVisible: boolean;
  roundEffectsVisible: boolean;
  centered: boolean;
  areasColored: boolean;
  areasFilled: boolean;
  backgroundFilled: boolean;

  titleVisible?: boolean;
};

export type ChartDataMap = {
  simulation: number[];
  simulationRound: number[];
  target: number[];
  resource: number[];
  index: number[];
  actionActivation: string[];
  incidentActivation: string[];
  scheduledEffectActivation: string[];

  selectedItem?: SelectableChartItem;
  selectedSimulationId?: number;
};

export type ConfigurableType =
  | "target"
  | "resource"
  | "index"
  | "simulation"
  | "simulationRound"
  | "actionActivation"
  | "incidentActivation"
  | "scheduledEffectActivation";

export type ItemConfigMap = {
  [type in ConfigurableType]: {
    [id: number | string]: ItemConfig;
  };
};

export interface ChartLike {
  chartType: ChartType | null;
  dataToggles: {
    all: {
      target: boolean;
      resource: boolean;
      index: boolean;
      simulation: boolean;
      simulationRound: boolean;
    };
  };
  data: ChartDataMap;
  itemConfigs: ItemConfigMap;
  chartSettings: ChartSettings;
}

export interface ChartItem extends StoreItem, ChartLike {
  type: "chart";
  uid: Uid<"chart">;
}

// These indices are virtual indices that do not exist in simulations,
// but are used to represent special states in simulation charts.
export const VIRTUAL_START_ROUND_INDEX = -1;
export const VIRTUAL_END_ROUND_INDEX = -2;
export const ACTIVE_SIMULATION_ID = -1;

export const defaultDataToggles: ChartLike["dataToggles"] = {
  all: {
    target: false,
    resource: false,
    index: false,
    simulation: false,
    simulationRound: false
  }
};

export const defaultData: ChartDataMap = {
  simulation: [-1],
  simulationRound: [],
  target: [],
  resource: [],
  index: [],
  actionActivation: [],
  incidentActivation: [],
  scheduledEffectActivation: [],

  selectedSimulationId: -1
};

export const defaultItemConfigs: ItemConfigMap = {
  target: {},
  resource: {},
  index: {},
  simulation: {},
  actionActivation: {},
  incidentActivation: {},
  scheduledEffectActivation: {},
  simulationRound: {}
};

export const defaultChartSettings: ChartSettings = {
  zoomed: false,
  width: null,
  height: null,
  scaleColorsVisible: true,
  ticksVisible: true,
  xAxis: "simulationRound",
  yAxis: "value",
  xAxisLabelsShortened: true,
  xAxisLabelsVisible: true,
  xAxisTitleVisible: true,
  yAxisLabelsShortened: true,
  yAxisLabelsVisible: true,
  yAxisTitleVisible: true,
  groupLabelsVisible: true,
  groupLabelsShortened: true,
  filterZeroValues: false,
  subValuesVisible: false,
  endPointsVisible: true,
  roundEffectsVisible: true,
  centered: false,
  areasColored: false,
  areasFilled: true,
  fontSize: 12,
  backgroundFilled: true
};

export type PartialChartItem = {
  chartType?: ChartType | null;
  dataToggles?: {
    all?: Partial<ChartLike["dataToggles"]["all"]>;
  };
  chartSettings?: Partial<ChartSettings>;
  data?: Partial<ChartDataMap>;
  itemConfigs?: Partial<ItemConfigMap>;
};
